import React, { useState, useContext, useCallback } from "react";
import { useLocalStorage } from "../../App/hooks/useLocalStorage";

const FiltersContext = React.createContext();
const DEFAULT_ITEMS_PER_VIEW = 100;
const ITEMS_PER_VIEW = "itemsPerView";
const SHOW_TEMPORARY_IMPORT = "showTemporaryImport";

export function useFiltersContext() {
  return useContext(FiltersContext);
}

function readStoredFilters(storageKey) {
  if (!storageKey) return null;
  try {
    return JSON.parse(sessionStorage.getItem(storageKey));
  } catch (error) {
    return null;
  }
}

function getInitialFilters(storedFilters, showTemporaryImport) {
  const { [SHOW_TEMPORARY_IMPORT]: _, ...filters } = storedFilters?.filters || {};
  return showTemporaryImport ? { ...filters, [SHOW_TEMPORARY_IMPORT]: "true" } : filters;
}

export function FiltersProvider({ children, storageKey }) {
  const [persistedShowTemporaryImport, setPersistedShowTemporaryImport] = useLocalStorage(SHOW_TEMPORARY_IMPORT, false);
  const [storedFilters] = useState(() => readStoredFilters(storageKey));

  const [applyFilters, setApplyFilters] = useState(() =>
    getInitialFilters(storedFilters, persistedShowTemporaryImport)
  );
  const [pendingFilters, setPendingFilters] = useState(() =>
    getInitialFilters(storedFilters, persistedShowTemporaryImport)
  );
  const [selections, setSelections] = useState(() => storedFilters?.selections || {});
  const [mandatoryFilters, setMandatoryFilters] = useState({});

  const saveFilter = useCallback(
    (filter) => {
      setPendingFilters((existing) => {
        if (filter.values.length === 0) return existing;

        return {
          ...existing,
          [filter.name]: filter.values.join(","),
        };
      });

      if (filter.items) {
        setSelections((existing) => ({ ...existing, [filter.name]: filter.items }));
      }

      if (filter.name === SHOW_TEMPORARY_IMPORT) {
        setPersistedShowTemporaryImport(true);
      }
    },
    [setPendingFilters, setPersistedShowTemporaryImport]
  );

  const removeFilter = useCallback(
    (filterName) => {
      setPendingFilters((existing) => {
        if (Object.keys(existing).length === 0 && typeof existing === "object" && existing != null) return {};

        const cleanedFilters = { ...existing };
        delete cleanedFilters[filterName];
        return cleanedFilters;
      });

      setSelections((existing) => {
        const cleanedSelections = { ...existing };
        delete cleanedSelections[filterName];
        return cleanedSelections;
      });

      if (filterName === SHOW_TEMPORARY_IMPORT) {
        setPersistedShowTemporaryImport(false);
      }
    },
    [setPendingFilters, setPersistedShowTemporaryImport]
  );

  const clearFilters = () => {
    setApplyFilters(mandatoryFilters);
    setPendingFilters({});
    setSelections({});
    setPersistedShowTemporaryImport(false);
    if (storageKey) sessionStorage.removeItem(storageKey);
  };

  const applyPendingFilters = useCallback(() => {
    setApplyFilters({ ...mandatoryFilters, ...pendingFilters });
    if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify({ filters: pendingFilters, selections }));
  }, [mandatoryFilters, pendingFilters, selections, storageKey]);

  const [itemsPerView, setItemsPerView] = useLocalStorage(ITEMS_PER_VIEW, DEFAULT_ITEMS_PER_VIEW);

  return (
    <FiltersContext.Provider
      value={{
        applyFilters,
        selections,
        setApplyFilters,
        removeFilter,
        clearFilters,
        saveFilter,
        itemsPerView,
        setItemsPerView,
        applyPendingFilters,
        setMandatoryFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
}
